import { z } from 'zod';
import { createToolSignature, ToolSignature } from './tool-signature';

export const searchConversationsToolSignature = createToolSignature({
    name: 'searchConversationsTool',
    description: undefined,
    inputSchema: z.object({
        query: z
            .string()
            .describe(
                'The search query (e.g., "find conversations needing follow-up", "search for messages about pricing", "find all mentions of product launch", "get full conversation with Prospect ID: 12345")',
            ),
    }),
    outputSchema: z.string(),
    toolCallUi: (input) => `Searching conversations`,
});

// const ConversationMessageSchema = z.object({
//     content: z.string().nullable().describe('The content of the message. '),
//     sentOn: z.date().nullable().describe('The date and time the message was sent.'),
//     direction: z
//         .string()
//         .describe(
//             'The direction of the message. "in" if the message is from the prospect, "out" if the message is to the prospect.',
//         ),
//     messageType: z
//         .enum(Object.values(ActionType) as [string, ...string[]])
//         .nullable()
//         .describe('The type of message.'),
// });

const ConversationSchema = z.object({
    prospectId: z.string().describe('The prospectId of the contact that the conversation is with.'),
    prospectFullName: z.string().nullable().describe('The full name of the contact that the conversation is with.'),
    messages: z.array(z.string()).describe('The messages in the conversation between the user and the contact.'),
});

export const searchForMessagesToolSignature = createToolSignature({
    name: 'searchForMessagesTool',
    description:
        'Search for messages inside the users LinkedIn inbox and their email (if they have connected it to Andsend). Search for mentions of words, and/or filter out messages within a date range. Only returns individual messages that match the search query, and does not return the rest of the conversation (more messages may exist in the conversation).',
    inputSchema: z.object({
        searchText: z
            .string()
            .describe(
                'The text or phrase to search for in message content (empty string if you want to search for all messages). Supports operators: & (AND), | (OR), ! (NOT), <-> (followed by), :* (prefix match). Examples: "fox & dog", "fox | cat", "!cat", "quick <-> fox". Filter only applies on message content and no other fields.',
            ),
        includeSubjectLines: z
            .boolean()
            .describe('Whether to search in subject lines as well as in the body of the message'),
        numberOfResults: z.number().describe('The number of matching messages to return'),
        sentBefore: z
            .string()
            .describe(
                'Only return messages sent before this date (ISO String), empty string if you want to search for all messages',
            ),
        sentAfter: z
            .string()
            .describe(
                'Only return messages sent after this date (ISO String), empty string if you want to search for all messages. Could be useful to search for recent dates if you want to find messages from the past few days.',
            ),
    }),
    outputSchema: z.object({
        conversations: z.array(ConversationSchema).describe('The conversations found.'),
        totalMatches: z.number().describe('The total number of matches found.'),
        query: z.string().describe('The query used to search for messages.'),
    }),
    toolCallUi: (input) => `Searching conversations`,
});

export const getConversationsByProspectIdsToolSignature = createToolSignature({
    name: 'getConversationsByProspectIdsTool',
    description:
        'Retrieves the complete conversation history with specific prospects. Use this when you need all messages exchanged with prospects.',
    inputSchema: z.object({
        prospectIds: z.array(z.string()).describe('The IDs of the prospects whose conversations you want to retrieve'),
        limit: z.number().optional().describe('Optional: Maximum number of messages to retrieve (default: 20)'),
    }),
    outputSchema: z.object({
        conversations: z.array(ConversationSchema).describe('The conversations found.'),
    }),
    toolCallUi: (input) => `Getting conversations`,
});

export const getUsersConversationsToolSignature = createToolSignature({
    name: 'getUsersConversationsTool',
    description:
        "Gets the all the recent conversations the user has had with any contact. Returns a list of up to 40 recent conversations with the last 5 messages per conversation. Useful for browsing through the user's conversations.",
    inputSchema: z.object({
        page: z.number().min(1).optional().describe('The page number to retrieve. Starts at 1.'),
    }),
    outputSchema: z.object({
        conversations: z.array(ConversationSchema),
    }),
    toolCallUi: (input) => `Getting conversations`,
});
